/**
 * Build step 5 — usage upload, utilisation, forecast and alerts (ADR-001 §11.7, §8.3, §10.1).
 * Visibility follows the client (managers all; an ops_member their own; requesters/HR none). Revenue never
 * appears in a usage payload. Every write is audited (real actor + acting-as).
 */
import { IdempotencyHeaders } from './common.js';
import { ClientProductManager, ClientProductMember } from './clients.js';
import { AlertActionBody, Alert, AlertIdParams, AlertPage, AliasParams, ApplyUploadBody, ApplyUploadResponse, ClientIdOnly, ClientProductsBatchQuery, ClientProductParams, ClientUsage, ClientUsageQuery, ClientUsagesQuery, CreateAliasBody, CreateClientProductBody, CreateProductBody, FieldAlias, FieldAliasList, ListAlertsQuery, ListUploadsQuery, ManualUsageBody, ParseResponse, Product, ProductKeyParams, ProductList, SignUploadBody, SignUploadResponse, TemplateParams, UpdateClientProductBody, UpdateProductBody, UploadDetail, UploadIdParams, UploadsPage, UsageLogEntry, UsageLogPage, UsageLogQuery, UsageOverview, UsageOverviewQuery, DevFileParams, } from './usage.js';
import { list, Uuid } from './common.js';
import { z } from 'zod';
import { API_BASE, defineRoute } from './route.js';
const NON_REQUESTER = ['clients.read.all', 'clients.read.own'];
const MANAGER = ['clients.read.all'];
const SCOPE = 'Managers; or the client’s current SPOC. Others: 404.';
const ClientProductListManager = list(ClientProductManager);
const ClientProductListMember = list(ClientProductMember);
const ClientProductList = list(z.union([ClientProductManager, ClientProductMember])).meta({ id: 'ClientProductList' });
const ClientProductAny = z.union([ClientProductManager, ClientProductMember]).meta({ id: 'ClientProduct' });
/** One client's products, for the batched read: the client id, then exactly what clientProducts.list returns for it. */
const batchOf = (cp) => list(z.object({ client_id: Uuid, products: z.array(cp) }).strict());
const ClientProductsBatchManager = batchOf(ClientProductManager);
const ClientProductsBatchMember = batchOf(ClientProductMember);
const ClientProductsBatch = batchOf(ClientProductAny).meta({ id: 'ClientProductsBatch' });
const ClientUsageList = list(ClientUsage).meta({ id: 'ClientUsageList' });
export const step5Routes = {
    // ============================================================ products
    'products.list': defineRoute({
        id: 'products.list',
        method: 'GET',
        path: `${API_BASE}/products`,
        summary: 'The products and their thresholds, fields and accepted headers',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers and ops members. Others: 403.' },
        success: { status: 200, description: 'Products in display order', schema: ProductList },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'products.create': defineRoute({
        id: 'products.create',
        method: 'POST',
        path: `${API_BASE}/products`,
        summary: 'Add a product',
        description: 'watch_line is saved as max(60, low_line + 10) (prototype rule); `adjustment` says when it was raised. 409 on a duplicate key.',
        access: { auth: 'session', anyOf: ['settings.write'], note: 'Managers. Others: 403.' },
        headers: IdempotencyHeaders,
        body: CreateProductBody,
        success: { status: 201, description: 'Created', schema: Product },
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'products.update': defineRoute({
        id: 'products.update',
        method: 'PATCH',
        path: `${API_BASE}/products/{key}`,
        summary: 'Change a product’s thresholds, names, source or extra fields',
        description: 'Products are not versioned (settings, last write wins, audited). watch_line rule as on create.',
        access: { auth: 'session', anyOf: ['settings.write'], deny: 'forbidden', note: 'Managers. Others: 403.' },
        params: ProductKeyParams,
        body: UpdateProductBody,
        success: { status: 200, description: 'Updated', schema: Product },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'aliases.list': defineRoute({
        id: 'aliases.list',
        method: 'GET',
        path: `${API_BASE}/products/{key}/field-aliases`,
        summary: 'Accepted report headers for a product’s fields (the fix for mismatched real headers)',
        access: { auth: 'session', anyOf: ['settings.write'], deny: 'forbidden', note: 'Managers. Others: 403.' },
        params: ProductKeyParams,
        success: { status: 200, description: 'Aliases', schema: FieldAliasList },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'aliases.create': defineRoute({
        id: 'aliases.create',
        method: 'POST',
        path: `${API_BASE}/products/{key}/field-aliases`,
        summary: 'Accept another header name for a field — a data insert, no deploy',
        description: '409 when the normalised alias already exists for the product.',
        access: { auth: 'session', anyOf: ['settings.write'], deny: 'forbidden', note: 'Managers. Others: 403.' },
        params: ProductKeyParams,
        headers: IdempotencyHeaders,
        body: CreateAliasBody,
        success: { status: 201, description: 'The alias', schema: FieldAlias },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'aliases.delete': defineRoute({
        id: 'aliases.delete',
        method: 'DELETE',
        path: `${API_BASE}/products/{key}/field-aliases/{id}`,
        summary: 'Stop accepting a header name',
        access: { auth: 'session', anyOf: ['settings.write'], deny: 'forbidden', note: 'Managers. Others: 403.' },
        params: AliasParams,
        success: { status: 204, description: 'Removed' },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ client products
    'clientProducts.list': defineRoute({
        id: 'clientProducts.list',
        method: 'GET',
        path: `${API_BASE}/clients/{id}/products`,
        summary: 'The products a client buys (mrr_lakh for managers only)',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: ClientIdOnly,
        success: {
            status: 200,
            description: 'Client products',
            schema: ClientProductList,
            byAudience: { revenue: ClientProductListManager, other: ClientProductListMember },
        },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clientProducts.batch': defineRoute({
        id: 'clientProducts.batch',
        method: 'GET',
        path: `${API_BASE}/client-products`,
        summary: 'The products of many clients in one read (Settings › Products and usage, Settings › Integrations)',
        description: 'Without ?client_ids: every active client the caller can see, each with its products (an empty list when it buys ' +
            'nothing), ordered by client name. With ?client_ids (at most 200): those of the named clients the caller can see, ' +
            'whatever their status; ids the caller cannot see are left out, never confirmed. Each client\'s products are ' +
            'exactly what GET /clients/{id}/products returns for it (mrr_lakh for managers only). One request per screen, ' +
            'whatever the client count (t-c1371dd8).',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers → every client; ops_member → their own. Requesters and HR: 403.' },
        query: ClientProductsBatchQuery,
        success: {
            status: 200,
            description: 'Client products, grouped by client',
            schema: ClientProductsBatch,
            byAudience: { revenue: ClientProductsBatchManager, other: ClientProductsBatchMember },
        },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'clientProducts.create': defineRoute({
        id: 'clientProducts.create',
        method: 'POST',
        path: `${API_BASE}/clients/{id}/products`,
        summary: 'Add a product the client buys',
        description: '409 when the client already has that product. A member sending mrr_lakh gets 403 with field_errors.',
        // Open to members so the SPOC gets an honest 403 (a param route denied by anyOf answers 404); the handler
        // finds the client first (404 when the caller cannot see it), then refuses anyone who is not a manager.
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers. The SPOC: 403; others 404.' },
        params: ClientIdOnly,
        headers: IdempotencyHeaders,
        body: CreateClientProductBody,
        success: { status: 201, description: 'Added', schema: ClientProductAny, byAudience: { revenue: ClientProductManager, other: ClientProductMember } },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'clientProducts.update': defineRoute({
        id: 'clientProducts.update',
        method: 'PATCH',
        path: `${API_BASE}/clients/{id}/products/{cp_id}`,
        summary: 'Change plan, base, source, status (and mrr_lakh for managers)',
        description: 'Not versioned (last write wins, audited). A member sending mrr_lakh gets 403 with field_errors.',
        access: { auth: 'session', anyOf: MANAGER, note: 'Managers. Others: 404.' },
        params: ClientProductParams,
        body: UpdateClientProductBody,
        success: { status: 200, description: 'Updated', schema: ClientProductAny, byAudience: { revenue: ClientProductManager, other: ClientProductMember } },
        errors: ['no_session', 'not_found', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ usage views
    'usage.overview': defineRoute({
        id: 'usage.overview',
        method: 'GET',
        path: `${API_BASE}/usage/overview`,
        summary: 'Usage → Overview: every client × every product, coloured by utilisation, with trend and data freshness',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers → all; ops_member → own clients. Others: 403.' },
        query: UsageOverviewQuery,
        success: { status: 200, description: 'The heat grid', schema: UsageOverview },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'usage.client': defineRoute({
        id: 'usage.client',
        method: 'GET',
        path: `${API_BASE}/usage/clients/{id}`,
        summary: 'The client usage page: per product used/plan/util, vs last month, forecast, 6 months, 14 days, extras, tip',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: ClientIdOnly,
        query: ClientUsageQuery,
        success: { status: 200, description: 'Per-product usage', schema: ClientUsage },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'usage.clients': defineRoute({
        id: 'usage.clients',
        method: 'GET',
        path: `${API_BASE}/usage/clients`,
        summary: 'Many clients\' usage pages in one read (Money › Usage, Usage › Alerts)',
        description: 'Without ?client_ids: every active client the caller can see (the clients of GET /usage/overview), ordered by ' +
            'client name. With ?client_ids (at most 200): those of the named clients the caller can see, whatever their ' +
            'status; ids the caller cannot see are left out, never confirmed. "Can see" is exactly GET /usage/clients/{id}\'s ' +
            'rule, and each item is exactly what that route returns for the client. One request per screen, whatever the ' +
            'client count (t-b083972d).',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers; or the client’s current SPOC (as GET /usage/clients/{id}). Others are left out. Requesters and HR: 403.' },
        query: ClientUsagesQuery,
        success: { status: 200, description: 'Per-client usage, one item per client', schema: ClientUsageList },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'usage.alerts': defineRoute({
        id: 'usage.alerts',
        method: 'GET',
        path: `${API_BASE}/usage/alerts`,
        summary: 'Predictive alerts, worst first, each with its rule, numbers and why',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers → all; ops_member → own clients. Others: 403.' },
        query: ListAlertsQuery,
        success: { status: 200, description: 'A page of alerts, worst first', schema: AlertPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'usage.alertAck': defineRoute({
        id: 'usage.alertAck',
        method: 'POST',
        path: `${API_BASE}/usage/alerts/{id}/ack`,
        summary: 'Acknowledge an alert (seen, working on it)',
        description: 'Idempotent on an acknowledged alert; 409 on a resolved one.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: AlertIdParams,
        body: AlertActionBody,
        success: { status: 200, description: 'The alert', schema: Alert },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'usage.alertResolve': defineRoute({
        id: 'usage.alertResolve',
        method: 'POST',
        path: `${API_BASE}/usage/alerts/{id}/resolve`,
        summary: 'Resolve an alert (the nightly run reopens it if the rule still fires)',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: SCOPE },
        params: AlertIdParams,
        body: AlertActionBody,
        success: { status: 200, description: 'The alert', schema: Alert },
        errors: ['no_session', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ report upload
    'uploads.sign': defineRoute({
        id: 'uploads.sign',
        method: 'POST',
        path: `${API_BASE}/usage-reports/sign`,
        summary: 'Get an upload slot for a product’s report (the browser sends the file directly)',
        description: '422 over 10 MB or on an extension the purpose does not take. purpose report (default): .xlsx .csv .tsv .txt, read by ' +
            'parse. purpose attachment (proof for POST /usage/manual file_key): also .pdf .png .jpg .jpeg, with a content type of ' +
            'PDF, PNG, JPEG, CSV, TSV, plain text, Excel or application/octet-stream (never HTML, SVG or script); kept, never ' +
            'parsed, not listed or addressable under /usage-reports. The month is chosen here, never read from the file.' +
            ' 429 rate_limited (Postgres file store only) when the caller’s, or everyone’s, uploads not yet attached to anything are at their cap (FILES_PENDING_USER_MB / FILES_PENDING_TOTAL_MB), or the caller’s link uploads of the last 24 hours, attached or not, are at their day cap (FILES_UPLOAD_USER_DAY_MB); uploads nothing uses are deleted after 24 hours.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers and SPOCs. Others: 403.' },
        headers: IdempotencyHeaders,
        body: SignUploadBody,
        success: { status: 201, description: 'The upload slot', schema: SignUploadResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'rate_limited', 'upstream_unavailable'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'uploads.parse': defineRoute({
        id: 'uploads.parse',
        method: 'POST',
        path: `${API_BASE}/usage-reports/{id}/parse`,
        summary: 'Parse the uploaded file (in the worker; poll GET /usage-reports/{id})',
        description: 'Idempotent while parsing; 409 once applied or discarded; 422 when the file has not arrived.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'The uploader or a manager. Others: 404.' },
        params: UploadIdParams,
        success: { status: 202, description: 'Parsing', schema: ParseResponse },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'uploads.get': defineRoute({
        id: 'uploads.get',
        method: 'GET',
        path: `${API_BASE}/usage-reports/{id}`,
        summary: 'The staged rows to review: match, numbers, why a row did not match',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'The uploader or a manager. Others: 404.' },
        params: UploadIdParams,
        success: { status: 200, description: 'Upload and rows', schema: UploadDetail },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'uploads.apply': defineRoute({
        id: 'uploads.apply',
        method: 'POST',
        path: `${API_BASE}/usage-reports/{id}/apply`,
        summary: 'Apply the reviewed rows (idempotent upsert per client, product and month)',
        description: '409 unless staged or applied. Health is recomputed for the clients touched.',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'The uploader or a manager. Others: 404.' },
        params: UploadIdParams,
        body: ApplyUploadBody,
        success: { status: 200, description: 'Applied', schema: ApplyUploadResponse },
        errors: ['no_session', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'uploads.discard': defineRoute({
        id: 'uploads.discard',
        method: 'POST',
        path: `${API_BASE}/usage-reports/{id}/discard`,
        summary: 'Discard a staged upload (the original file is kept)',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'The uploader or a manager. Others: 404.' },
        params: UploadIdParams,
        success: { status: 204, description: 'Discarded' },
        errors: ['no_session', 'not_found', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'uploads.list': defineRoute({
        id: 'uploads.list',
        method: 'GET',
        path: `${API_BASE}/usage-reports`,
        summary: 'Usage → Reports and upload: uploads, and per product “In this month / Report due / Not uploaded yet”',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers → all; ops_member → own uploads. Others: 403.' },
        query: ListUploadsQuery,
        success: { status: 200, description: 'A page of uploads plus the per-product summary', schema: UploadsPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'usage.log': defineRoute({
        id: 'usage.log',
        method: 'GET',
        path: `${API_BASE}/usage/log`,
        summary: 'Reports → “Reports entered”: every number entered (client, product, period, numbers, who, report file)',
        description: 'Newest first (by entry time). `original_filename` is the report file the numbers came from; `null` when typed in. ' +
            'Filter by `product_key` and/or `client_id`; a `client_id` outside the caller’s scope yields an empty page.',
        access: {
            auth: 'session',
            anyOf: NON_REQUESTER,
            note: 'Managers → all; ops_member → entries for clients they are SPOC of, plus entries they made themselves. Others: 403.',
        },
        query: UsageLogQuery,
        success: { status: 200, description: 'A page of usage entries', schema: UsageLogPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'usage.manual': defineRoute({
        id: 'usage.manual',
        method: 'POST',
        path: `${API_BASE}/usage/manual`,
        summary: 'Type in a client’s number for a month (the manual path)',
        description: 'Upserts the (client, product, period) entry. 422 when the client has not bought the product. A file_key must be ' +
            'a completed upload made by the caller (422 otherwise), normally signed with purpose attachment (PDF, Excel, CSV or screenshot).',
        access: { auth: 'session', anyOf: NON_REQUESTER, note: 'Managers, or the client’s SPOC. Others: 403/404.' },
        headers: IdempotencyHeaders,
        body: ManualUsageBody,
        success: { status: 201, description: 'The entry', schema: UsageLogEntry },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'usage.template': defineRoute({
        id: 'usage.template',
        method: 'GET',
        path: `${API_BASE}/usage-report-template/{product_key}.xlsx`,
        summary: 'The report template for a product: its exact headers, one example row and number checks',
        description: 'Generated from the product’s fields and aliases, so it can never drift from what the parser accepts.',
        access: { auth: 'session', anyOf: NON_REQUESTER, deny: 'forbidden', note: 'Managers and ops members. Others: 403.' },
        params: TemplateParams,
        success: {
            status: 200,
            description: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'Content-Disposition': 'attachment; filename="{product_key}-usage-template.xlsx"' },
        },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ same-origin file reads (production and local)
    'files.get': defineRoute({
        id: 'files.get',
        method: 'GET',
        path: `${API_BASE}/files/{token}`,
        summary: 'Read a stored file through the API (the same-origin link every "open / play" endpoint returns)',
        description: 'The link behind `decks.file`, `documents.fileUrl`, `internalRequests.fileUrl` and ' +
            '`meetings.mediaUrl`, so ' +
            'a page that is cross-origin isolated (COOP/COEP) can play or preview it: it is served from the app’s own origin ' +
            'rather than from object storage. The `{token}` is signed by the API, names one object and the signed-in user it ' +
            'was issued to, and expires with the issuing endpoint’s TTL (5 or 10 minutes). It works only in the session of ' +
            'that same user (a viewed-as or acted-as session counts as the real signed-in person), so a copied link is ' +
            'useless to anyone else: 403 for a bad, expired or someone else’s token, with no detail about which. 404 when ' +
            'nothing is stored. The bytes are streamed, never held whole. Supports one `Range: bytes=a-b | a- | -n`: ' +
            '206 with `Content-Range` and `Accept-Ranges: bytes` (Safari needs it to play audio); a range outside the ' +
            'file is 416 with `Content-Range: bytes */size`. Content-Type is the server’s own, by the key’s extension ' +
            '(never the uploader’s; unknown → `application/octet-stream`). Content-Disposition is `inline` for PDF, PNG/JPEG ' +
            'and audio and `attachment` otherwise, unless the issuing endpoint signed the link as a download: `decks.file` ' +
            'always gets `attachment`, even for its PDF. The filename is the one the issuer signed into the link when it ' +
            'named one (`decks.file`: the deck’s contract name “<client> — Monthly|Quarterly review <period>.<ext>”; ' +
            '`documents.fileUrl` and `internalRequests.fileUrl`: the uploader’s original filename), else the key’s own ' +
            'name; the holder of a link cannot change it. It is sent as an ASCII-safe `filename="…"` (no control ' +
            'characters; accents dropped, dashes as `-`, any other non-ASCII, quote, backslash or slash as `_`; at most 150 ' +
            'characters) plus, when that is not the exact name, ' +
            '`filename*=UTF-8\'\'…` with the exact name (RFC 6266 / RFC 5987), which browsers prefer. Sent with ' +
            '`Cross-Origin-Resource-Policy: same-origin`, `X-Content-Type-Options: nosniff` and `Cache-Control: private, no-store`.',
        access: { auth: 'session', note: 'Any signed-in user, and only with a token issued to them. Others: 403.' },
        params: DevFileParams,
        success: {
            status: 200,
            description: 'The file’s bytes (206 for a Range request)',
            headers: {
                'Content-Type': 'The file’s type by extension (server-chosen)',
                'Content-Disposition': 'inline for PDF, PNG/JPEG and audio, attachment otherwise; always attachment when the link was issued as a ' +
                    'download (decks.file). filename="<ASCII-safe name>" — the name signed into the link (deck contract name, ' +
                    'uploader’s original filename) or the key’s own — plus filename*=UTF-8\'\'<exact name> (RFC 5987) when the ' +
                    'ASCII form is not exact',
                'Content-Length': 'Bytes in this response',
                'Accept-Ranges': 'bytes',
                'Content-Range': '206 only: bytes a-b/total',
                'Cross-Origin-Resource-Policy': 'same-origin',
                'X-Content-Type-Options': 'nosniff',
                'Cache-Control': 'private, no-store',
            },
        },
        errors: ['no_session', 'forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ files kept in Postgres (FILE_STORE=postgres)
    'files.put': defineRoute({
        id: 'files.put',
        method: 'PUT',
        path: `${API_BASE}/files/{token}`,
        summary: 'Receive an uploaded file where files are kept in Postgres',
        description: 'Registered only when the file store is Postgres (FILE_STORE=postgres), in production too. The API’s own ' +
            'stand-in for a presigned Spaces PUT: every upload slot (`uploads.sign`, a meeting or ' +
            'document upload, …) returns this URL with a signed token valid 15 minutes that names the object key, the ' +
            'Content-Type and the most bytes allowed, and the browser PUTs the raw bytes here exactly as it would to Spaces. ' +
            'The body is written to the database as it arrives, 1 MiB at a time, and stored only once it is complete; a ' +
            'replaced file is swapped in one step. 403 on a bad, forged or expired token, or a Content-Type other than the ' +
            'one the slot gave (as a Spaces signature would); 422 when the body is bigger than the slot allows (nothing is kept); 429 rate_limited when the ' +
            'uploads not yet attached to anything (the slot’s person’s, or everyone’s) are at their cap — checked before a ' +
            'byte is written, so slots handed out together cannot overfill it.',
        access: { auth: 'none', note: 'Anyone holding a valid signed upload token, and only where the route exists.' },
        params: DevFileParams,
        success: { status: 200, description: 'Stored' },
        errors: ['forbidden', 'validation_failed', 'rate_limited'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ local file store (non-production only)
    'files.devPut': defineRoute({
        id: 'files.devPut',
        method: 'PUT',
        path: `${API_BASE}/dev/files/{token}`,
        summary: 'Receive a file where object storage is not configured (local and test environments only)',
        description: 'NOT REGISTERED IN PRODUCTION, and only when Spaces is not configured. The local stand-in for a presigned ' +
            'Spaces PUT: `uploads.sign` returns this URL with a signed, expiring token naming the object key, and the ' +
            'browser PUTs the raw bytes here exactly as it would to Spaces. 403 on a bad or expired token, or a Content-Type ' +
            'other than the one the slot gave (as a Spaces signature would); 422 over 10 MB.',
        access: { auth: 'none', note: 'Anyone holding a valid signed token, and only where the route exists.' },
        params: DevFileParams,
        success: { status: 200, description: 'Stored' },
        errors: ['forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'write',
        devOnly: true,
    }),
    'files.devGet': defineRoute({
        id: 'files.devGet',
        method: 'GET',
        path: `${API_BASE}/dev/files/{token}`,
        summary: 'Read a file where object storage is not configured (local and test environments only)',
        description: 'NOT REGISTERED IN PRODUCTION, and only when Spaces is not configured. The local stand-in for a presigned ' +
            'Spaces GET (e.g. `meetings.mediaUrl`): a signed, expiring read token naming the object key. An upload token ' +
            'never reads, and a read token never writes. Answered as a Spaces read link is: the server’s type by the key’s ' +
            'extension (never the uploader’s), attachment unless PDF, PNG/JPEG or audio. 403 on a bad or expired token; ' +
            '404 when nothing is stored.',
        access: { auth: 'none', note: 'Anyone holding a valid signed read token, and only where the route exists.' },
        params: DevFileParams,
        success: { status: 200, description: 'The file’s bytes' },
        errors: ['forbidden', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
        devOnly: true,
    }),
};
//# sourceMappingURL=routes.step5.js.map